/**
 * BASTN shared constants.
 *
 * Decisions captured in .planning/phases/01-foundation/01-CONTEXT.md:
 * - BASTN_GOLD: brand accent color #d4af37 (charter site --accent) converted to
 *   decimal for Discord's embed color API. Used by every embed builder.
 *   Corrected in Phase 7 Plan 07-05 from 13938999 (0xd4b137) to 13938487 (0xd4af37).
 * - BASTN_TIMEZONE: IANA timezone string for BASTN's primary raid/event timezone.
 *   Seeded into D1 guild_config table in plan 01-03. Read by Phase 3 schedule editor
 *   and Phase 5 cron engine.
 *
 * Phase 2 additions:
 * - ERROR_CODES: canonical error code strings shared between guardian-worker and SPA.
 * - AUDIT_ACTIONS: audit_log action naming convention (<entityType>.<verb>).
 *   Phase 2 adds auth.login + auth.logout; 02-02 appends CRUD action constants.
 */

/**
 * BASTN_GOLD: 13938487 = 0xd4af37 = #d4af37 — matches charter site --accent.
 *
 * NOTE: Pre-Phase-7 the value was 13938999 (= 0xd4b137 — incorrect).
 * Migration 0009_bastn_gold_fix.sql REPLACEs the old value in stored embed_json
 * rows. New embeds use this constant directly.
 */
export const BASTN_GOLD = 13938487 as const;

// IANA timezone string. Seeded into guild_config in plan 01-03.
export const BASTN_TIMEZONE = 'America/New_York' as const;

// ---------------------------------------------------------------------------
// Error codes (Phase 2 — guardian-worker HTTP API)
// ---------------------------------------------------------------------------

/**
 * Canonical error codes for the { error: { code, message } } response shape.
 * Shared between guardian-worker (emits) and SPA (handles).
 * All non-2xx responses from guardian-worker use one of these codes.
 */
export const ERROR_CODES = {
  SESSION_EXPIRED:     'session_expired',
  ROLE_REQUIRED:       'role_required',
  OAUTH_STATE_INVALID: 'oauth_state_invalid',
  DISCORD_UNAVAILABLE: 'discord_unavailable',
  VALIDATION_FAILED:   'validation_failed',
  NOT_FOUND:           'not_found',
  INTERNAL_ERROR:      'internal_error',
  // Phase 4: synced from apps/guardian-worker/src/errors.ts (Finding B)
  NAME_TAKEN:          'name_taken',
  DISPATCH_FAILED:     'dispatch_failed',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

// ---------------------------------------------------------------------------
// Audit action names (Phase 2 — guardian-worker audit_log writes)
// ---------------------------------------------------------------------------

/**
 * Audit log action names following the <entityType>.<verb> convention.
 * Phase 2 adds auth.login + auth.logout.
 * Plan 02-02 appends: REMINDER_CREATE, REMINDER_UPDATE, REMINDER_DELETE,
 *   REMINDER_POST_NOW, COMMAND_CREATE, COMMAND_UPDATE, COMMAND_DELETE.
 *
 * Phase 4's audit log viewer can prefix-filter by entity type (e.g., 'reminder.*').
 */
export const AUDIT_ACTIONS = {
  AUTH_LOGIN:          'auth.login',
  AUTH_LOGOUT:         'auth.logout',
  REMINDER_CREATE:     'reminder.create',
  REMINDER_UPDATE:     'reminder.update',
  REMINDER_DELETE:     'reminder.delete',
  REMINDER_POST_NOW:   'reminder.post_now',
  COMMAND_CREATE:      'command.create',
  COMMAND_UPDATE:      'command.update',
  COMMAND_DELETE:      'command.delete',
  // Phase 4 additions (Finding C):
  REMINDER_DM_TEST:    'reminder.dm_test',  // Plan 04-02 dm-test handler writes this
  REMINDER_DISPATCH:   'reminder.dispatch', // Phase 5 will write; Plans 04-02/03 read this constant
  // Phase 5 additions (plan 05-00 / 05-02):
  REMINDER_AUTO_DISABLED: 'reminder.auto_disabled', // Phase 5 (plan 05-02) cron loop writes this when 3 consecutive non-429 failures auto-disable a reminder
  // Phase 7 additions (plan 07-00):
  MEMBER_ROLE_ADDED:   'member.role_added',   // /role-toggle handler writes this when it grants a role (CMD-07, plan 07-03)
  MEMBER_ROLE_REMOVED: 'member.role_removed', // /role-toggle handler writes this when it revokes a role (CMD-07, plan 07-03)
  COMMAND_RELOAD:      'command.reload',      // /reload admin command writes this on bulk-PUT re-registration (CMD-08, plan 07-04)
} as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[keyof typeof AUDIT_ACTIONS];

// ---------------------------------------------------------------------------
// Membercount tracked roles (Phase 7 — plan 07-00)
// ---------------------------------------------------------------------------

/**
 * Roles displayed by the /membercount command's role breakdown (CMD-06, plan 07-02).
 * v1: hardcoded list. Operator may override via deploy if BASTN's role taxonomy changes
 * (RESEARCH Open Question 2 — defer dynamic config to v2).
 *
 * Format: { id: string, label: string, emoji?: string }
 * The label is what appears in the embed field name; the emoji is optional decoration.
 *
 * Operator: append additional tracked roles here. Role IDs are obtainable via
 * `GET /guilds/{id}/roles` against the BASTN guild.
 */
export const MEMBERCOUNT_TRACKED_ROLES = [
  { id: '1400984502578184263', label: 'Officers (GM)' },
  { id: '1402382181388521483', label: 'Officers (Advisor)' },
] as const;

export type MembercountTrackedRole = (typeof MEMBERCOUNT_TRACKED_ROLES)[number];
